import { useState, useEffect } from 'react';
import { Search, Filter, Grid, List, ChevronDown } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import ShopCard from '../components/shops/ShopCard';
import ShopFilters from '../components/shops/ShopFilters';
import { ShopCardGridSkeleton, ShopCardListSkeleton } from '../components/shops/ShopCardSkeleton';
import { apiService } from '../services/api';

export default function ShopList() {
  const [shops, setShops] = useState<any[]>([]);
  const [filteredShops, setFilteredShops] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState('shopNumber');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    purpose: '',
    floor: '',
    minSize: '',
    maxSize: '',
    status: 'available'
  });

  useEffect(() => {
    loadShops();
  }, []);

  const loadShops = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getShops({ status: 'available', page: 1, limit: 100 });
      if (response.status === 'success' && response.data?.shops) {
        setShops(response.data.shops);
        setFilteredShops(response.data.shops);
      } else {
        setError('Failed to load shops');
      }
    } catch (err) {
      console.error('Error loading shops:', err);
      setError('Failed to load shops. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = shops.filter((shop) => {
      const matchesSearch = shop.purpose.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesPurpose = !filters.purpose || shop.purpose === filters.purpose;
      const matchesFloor = !filters.floor || shop.floor === filters.floor;
      const matchesStatus = !filters.status || shop.status === filters.status;
      const minSize = filters.minSize ? parseFloat(filters.minSize) : 0;
      const maxSize = filters.maxSize ? parseFloat(filters.maxSize) : Infinity;
      const matchesSize = shop.size >= minSize && shop.size <= maxSize;
      return matchesSearch && matchesPurpose && matchesFloor && matchesStatus && matchesSize;
    });
    filtered.sort((a, b) =>
      sortBy === 'size' ? b.size - a.size : a.shopNumber.localeCompare(b.shopNumber)
    );
    setFilteredShops(filtered);
  }, [shops, searchTerm, filters, sortBy]);

  const clearAllFilters = () => {
    setSearchTerm('');
    setFilters({ purpose: '', floor: '', minSize: '', maxSize: '', status: 'available' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface py-16 lg:py-20">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-ink mb-2">Available Shops</h1>
          <p className="text-ink-muted mb-8">Discover the right space for your business.</p>
          <div className="h-12 rounded-xl bg-surface-muted animate-pulse max-w-md mb-8" />
          {viewMode === 'grid' ? <ShopCardGridSkeleton /> : <ShopCardListSkeleton />}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center px-4">
        <div className="max-w-md w-full p-8 rounded-2xl bg-surface-raised shadow-card text-center">
          <p className="text-ink mb-4">{error}</p>
          <button
            type="button"
            onClick={loadShops}
            className="h-11 px-6 rounded-xl bg-primary text-white font-semibold hover:bg-primary-dark"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface py-16 lg:py-20">
      <Helmet>
        <title>Available Shops – The Gandi Mall</title>
        <meta name="description" content="Browse available retail and office spaces at Gandi Mall, Chinhoyi." />
      </Helmet>
      <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-ink mb-2">Available Shops</h1>
        <p className="text-ink-muted mb-8">Discover the right space for your business.</p>

        <div className="rounded-2xl bg-surface-raised shadow-card p-4 sm:p-6 mb-8">
          <div className="flex flex-col gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-subtle h-5 w-5" />
              <input
                type="search"
                placeholder="Search by business type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-stone-200 bg-surface focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-ink"
              />
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-3 rounded-xl border border-stone-200 bg-surface text-ink focus:ring-2 focus:ring-primary"
              >
                <option value="shopNumber">Shop Number</option>
                <option value="size">Size (Largest)</option>
              </select>
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className={`inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium transition-colors ${
                  showFilters ? 'bg-primary text-white' : 'bg-surface-muted text-ink hover:bg-stone-200'
                }`}
              >
                <Filter size={18} /> Filters
                <ChevronDown className={`size-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </button>
              <div className="flex bg-surface-muted rounded-xl p-1">
                <button
                  type="button"
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-white shadow-card' : 'hover:bg-white/50'}`}
                  aria-label="Grid view"
                >
                  <Grid size={20} />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-white shadow-card' : 'hover:bg-white/50'}`}
                  aria-label="List view"
                >
                  <List size={20} />
                </button>
              </div>
            </div>
            {showFilters && (
              <div className="pt-4 border-t border-stone-200">
                <ShopFilters filters={filters} onFiltersChange={setFilters} />
              </div>
            )}
          </div>
        </div>

        <p className="text-ink-muted text-sm mb-6">
          {filteredShops.length} of {shops.length} shops
        </p>

        {filteredShops.length === 0 ? (
          <div className="rounded-2xl bg-surface-raised shadow-card p-12 text-center">
            <p className="text-ink-muted mb-6">No shops match your filters.</p>
            <button
              type="button"
              onClick={clearAllFilters}
              className="h-11 px-6 rounded-xl bg-primary text-white font-semibold hover:bg-primary-dark"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <div
            className={
              viewMode === 'grid'
                ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'
                : 'space-y-4'
            }
          >
            {filteredShops.map((shop, index) => (
              <ShopCard key={shop.id ?? shop._id ?? index} shop={shop} viewMode={viewMode} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
